import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus, Copy } from 'lucide-react';
import { useQuickReplies, type QuickReply } from '@/hooks/useQuickReplies';
import { SettingsCard } from './SettingsCard';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';
import { useTranslation } from "react-i18next";
import {
  MESSAGE_VARIABLES,
  SAMPLE_CONTACT,
  hasMessageVariables,
  renderMessageVariables,
} from '@/lib/messageVariables';

const QuickRepliesTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { replies, loading, createReply, updateReply, deleteReply } = useQuickReplies();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReply, setEditingReply] = useState<QuickReply | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    shortcut: '',
    category: '',
    is_active: true
  });
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const insertVariableAtCursor = (token: string) => {
    const el = contentRef.current;
    if (!el) {
      setFormData(prev => ({ ...prev, content: prev.content + token }));
      return;
    }
    const start = el.selectionStart ?? formData.content.length;
    const end = el.selectionEnd ?? formData.content.length;
    const next = formData.content.slice(0, start) + token + formData.content.slice(end);
    setFormData(prev => ({ ...prev, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const previewContent = formData.content
    ? renderMessageVariables(formData.content, SAMPLE_CONTACT)
    : '';
  const showVariablePreview = hasMessageVariables(formData.content);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToSubmit = {
      ...formData,
      shortcut: formData.shortcut || null,
      category: formData.category || null
    };

    if (editingReply) {
      const success = await updateReply(editingReply.id, dataToSubmit);
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createReply(dataToSubmit);
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (reply: QuickReply) => {
    setEditingReply(reply);
    setFormData({
      name: reply.name,
      content: reply.content,
      shortcut: reply.shortcut || '',
      category: reply.category || '',
      is_active: reply.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("quickReplies.confirmDelete"))) {
      await deleteReply(id);
    }
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const resetForm = () => {
    setEditingReply(null);
    setFormData({
      name: '',
      content: '',
      shortcut: '',
      category: '',
      is_active: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("quickReplies.title")}</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">{t("quickReplies.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
              <Plus className="w-4 h-4 mr-2" />
              {t("quickReplies.new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
                  {editingReply ? 'Editar Resposta Rápida' : 'Nova Resposta Rápida'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name">{t("quickReplies.name")}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t("quickReplies.namePlaceholder")}
                    className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                    required
                  />
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="content">{t("quickReplies.message")}</Label>
                  <Textarea
                    id="content"
                    ref={contentRef}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder={t("quickReplies.messagePlaceholder", { v1: "{{ nome }}", v2: "{{ primeiro_nome }}" })}
                    className="min-h-[100px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                    required
                  />

                  <div className="flex flex-col gap-1.5 w-full">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#475569] dark:text-[#A1A1AA]">{t("quickReplies.variables")}</span>
                      <span className="text-xs text-[#64748B] dark:text-[#71717A]">{t("quickReplies.variablesHint")}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {MESSAGE_VARIABLES.map((variable) => (
                        <button
                          key={variable.token}
                          type="button"
                          onClick={() => insertVariableAtCursor(variable.token)}
                          title={variable.description}
                          className="text-xs font-mono px-2 py-1 rounded bg-[#F1F5F9] text-[#334155] border border-[#E5E7EB] hover:bg-[#E2E8F0] transition-colors dark:bg-[#27272A]/70 dark:text-[#D4D4D8] dark:border-[#3F3F46] dark:hover:bg-[#3F3F46]"
                        >
                          {variable.token}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-[#64748B] dark:text-[#71717A]">
                      {t("quickReplies.variablesNote")}
                    </p>
                  </div>

                  {showVariablePreview && (
                    <div className="flex flex-col gap-1.5 w-full">
                      <span className="text-xs text-[#475569] dark:text-[#A1A1AA]">
                        {t("quickReplies.preview")}
                      </span>
                      <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-2 text-sm text-[#0F172A] whitespace-pre-wrap min-h-[40px] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA]">
                        {previewContent}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="shortcut">{t("quickReplies.shortcut")}</Label>
                  <Input
                    id="shortcut"
                    value={formData.shortcut}
                    onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                    placeholder="Ex: /oi"
                    className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  />
                  <p className="text-xs text-[#64748B] dark:text-[#71717A]">{t("quickReplies.shortcutHint")}</p>
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="category">{t("quickReplies.category")}</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder={t("quickReplies.categoryPlaceholder")}
                    className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">{t("quickReplies.activeReply")}</Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("quickReplies.cancel")}
                  </Button>
                  <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
                    {editingReply ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <SettingsCard>
        {loading ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("quickReplies.loading")}</div>
        ) : replies.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("quickReplies.empty")}</div>
        ) : (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]">
            {replies.map((reply) => (
              <div key={reply.id} className="p-4 hover:bg-[#F8FAFC] transition-colors duration-200 dark:hover:bg-[#3F3F46]/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">{reply.name}</h3>
                      {reply.shortcut && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-400 font-mono">
                          {reply.shortcut}
                        </span>
                      )}
                      {reply.category && (
                        <span className="text-xs px-2 py-1 rounded bg-[#F1F5F9] dark:bg-[#27272A] text-[#475569] dark:text-[#A1A1AA]">
                          {reply.category}
                        </span>
                      )}
                      {!reply.is_active && (
                        <span className="text-xs px-2 py-1 rounded-full bg-[#FEF2F2] text-[#DC2626] dark:bg-red-500/15 dark:text-[#F87171]">
                          {t("quickReplies.inactive")}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#64748B] dark:text-[#71717A] whitespace-pre-wrap">{reply.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopy(reply.content)}
                      className="flex justify-center items-center rounded-[8px] border-[1px] border-[#E5E7EB] dark:border-[#3F3F464D] bg-[#F1F5F9] dark:bg-[#27272A] w-[34px] h-[34px] hover:bg-[#F1F5F9] dark:hover:bg-[#3F3F46]"
                      title={t("quickReplies.copyMessage")}
                    >
                      <Copy size={16} className="text-[#64748B] dark:text-[#FAFAFA]" />
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white w-[34px] h-[34px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]" aria-label={t("quickReplies.replyActions")}>
                          <EllipsisVertical size={18} className="text-current" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="!bg-white p-1 w-[95px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
                        <button
                          onClick={() => handleEdit(reply)}
                          className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]"
                        >
                          {t("quickReplies.edit")}
                        </button>
                        <button
                          onClick={() => handleDelete(reply.id)}
                          className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
                        >
                          {t("quickReplies.delete")}
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default QuickRepliesTab;
